import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DebugToolTrigger } from "./DebugToolTrigger";
import { RBACDebugTab } from "./tabs/RBACDebugTab";
import { StorageDebugTab } from "./tabs/StorageDebugTab";
import { StateDebugTab } from "./tabs/StateDebugTab";
import { Shield, Database, Layers, Bug } from "lucide-react";

const DEBUG_TOOL_STORAGE_KEY = "justping_debug_tool";

export function FloatingDebugTool() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Check if debug tool should be enabled (from localStorage or keyboard shortcut)
  useEffect(() => {
    const stored = localStorage.getItem(DEBUG_TOOL_STORAGE_KEY);
    if (stored === "enabled") {
      setIsEnabled(true);
    }
  }, []);

  // Handle keyboard shortcut (Ctrl+Shift+D)
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "d") {
      event.preventDefault();

      setIsEnabled((prev) => {
        const newValue = !prev;
        if (newValue) {
          localStorage.setItem(DEBUG_TOOL_STORAGE_KEY, "enabled");
        } else {
          localStorage.removeItem(DEBUG_TOOL_STORAGE_KEY);
          setIsOpen(false);
        }
        return newValue;
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Don't render anything if not enabled
  if (!isEnabled) {
    return null;
  }

  return (
    <>
      {/* Floating trigger button */}
      <DebugToolTrigger onClick={() => setIsOpen(true)} />

      {/* Debug panel dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-primary" />
              Debug Tool
              <Badge variant="outline" className="ml-2 text-xs">
                RBAC Inspector
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Inspect application state, permissions, and storage for debugging
              RBAC issues.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="rbac" className="flex-1 flex flex-col min-h-0">
            <TabsList className="shrink-0 grid w-full grid-cols-3">
              <TabsTrigger value="rbac" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">RBAC</span>
              </TabsTrigger>
              <TabsTrigger value="storage" className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                <span className="hidden sm:inline">Storage</span>
              </TabsTrigger>
              <TabsTrigger value="state" className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                <span className="hidden sm:inline">State</span>
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 min-h-0 mt-4">
              <TabsContent value="rbac" className="h-full m-0">
                <RBACDebugTab />
              </TabsContent>
              <TabsContent value="storage" className="h-full m-0">
                <StorageDebugTab />
              </TabsContent>
              <TabsContent value="state" className="h-full m-0">
                <StateDebugTab />
              </TabsContent>
            </div>
          </Tabs>

          {/* Footer */}
          <div className="shrink-0 pt-4 border-t text-xs text-muted-foreground flex items-center justify-between">
            <span>Press Ctrl+Shift+D to toggle debug tool</span>
            <span>
              Enabled via{" "}
              {localStorage.getItem(DEBUG_TOOL_STORAGE_KEY)
                ? "localStorage"
                : "keyboard shortcut"}
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
